import type {
  CoachIdentityActivationState,
  CoachIdentityIngestionReport,
  CoachIdentityProfile,
  CoachIdentityReference,
  CoachPhilosophyProfile,
  CoachVoiceProfile,
} from "@/lib/behavior/behaviorContracts";
import {
  extractPersistedCoachIdentityReferences,
  extractPersistedCoachIdentityReport,
  extractPersistedFrameworkPackSnapshot,
  resolvePersistedVoiceCustomizationStatus,
  resolvePersistedCoachIdentityTemplateVersion,
} from "@/lib/behavior/coachIdentityPersistence";
import type {
  CoachLensSource,
  CoachTwinSource,
} from "@/lib/coachSessionReviewContract";

function trimText(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

export function buildCoachIdentityIngestionReport(params: {
  philosophyProfile: CoachPhilosophyProfile;
  voiceProfile: CoachVoiceProfile;
  references?: CoachIdentityReference[];
  issues?: CoachIdentityIngestionReport["issues"];
}): CoachIdentityIngestionReport {
  const references = params.references || [];
  const issues = params.issues || [];
  const parsedSectionCount = [
    params.philosophyProfile.present,
    params.voiceProfile.present,
  ].filter(Boolean).length;
  const parsedReferenceCount = references.filter((reference) => reference.status === "parsed").length;
  const hasError = issues.some((issue) => issue.level === "error");
  const hasWarning = issues.some((issue) => issue.level === "warning");

  let status: CoachIdentityIngestionReport["status"] = "empty";
  if (parsedSectionCount === 0 && parsedReferenceCount === 0 && hasError) {
    status = "failed";
  } else if (parsedSectionCount > 0 || parsedReferenceCount > 0) {
    status = hasError || hasWarning ? "partial" : "parsed";
  } else if (references.length > 0 || hasWarning) {
    status = hasError ? "failed" : "partial";
  }

  return {
    status,
    parsedSectionCount,
    referenceCount: references.length,
    parsedReferenceCount,
    issues,
  };
}

export function buildCoachIdentityProfileFromSources(params: {
  philosophyProfile: CoachPhilosophyProfile;
  voiceProfile: CoachVoiceProfile;
  coachLens?: CoachLensSource | null;
  coachTwin?: CoachTwinSource | null;
}): CoachIdentityProfile {
  const frameworkPack = extractPersistedFrameworkPackSnapshot(params.coachLens);
  const philosophyProfile: CoachPhilosophyProfile = frameworkPack
    ? {
        ...params.philosophyProfile,
        lens: {
          ...params.philosophyProfile.lens,
          philosophy: params.philosophyProfile.lens.philosophy || trimText(frameworkPack.preview.philosophySummary),
          frameworks: params.philosophyProfile.lens.frameworks.length > 0
            ? params.philosophyProfile.lens.frameworks
            : [{ name: frameworkPack.label, summary: trimText(frameworkPack.preview.philosophySummary) }],
          frameworkPackId: frameworkPack.id,
          frameworkPackLabel: frameworkPack.label,
          frameworkPack,
          knowledgeCoverage: "full",
        },
      }
    : params.philosophyProfile;
  const persistedVoiceCustomizationStatus = resolvePersistedVoiceCustomizationStatus(params.coachTwin);
  const voiceProfile: CoachVoiceProfile = {
    ...params.voiceProfile,
    customizationStatus: params.voiceProfile.customizationStatus || persistedVoiceCustomizationStatus,
    present: params.voiceProfile.present || persistedVoiceCustomizationStatus === "framework_default",
    source: params.voiceProfile.source === "default" && persistedVoiceCustomizationStatus === "framework_default"
      ? "framework_pack_default"
      : params.voiceProfile.source,
  };
  const references = extractPersistedCoachIdentityReferences(params.coachLens);
  const persistedReport = extractPersistedCoachIdentityReport(params.coachLens?.coach_lens_json?.identity_report);
  const templateVersion = resolvePersistedCoachIdentityTemplateVersion(params.coachTwin);

  return {
    present: philosophyProfile.present || voiceProfile.present,
    version: "coach_identity_v1",
    philosophy: philosophyProfile,
    voice: voiceProfile,
    references,
    ingestionReport: persistedReport || buildCoachIdentityIngestionReport({
      philosophyProfile,
      voiceProfile,
      references,
    }),
    activation: buildCoachIdentityActivationState({
      philosophyProfile,
      voiceProfile,
      status: philosophyProfile.present || voiceProfile.present ? "active" : "draft",
    }),
    templateVersion,
    rawTemplate: null,
  };
}

export function buildCoachIdentityActivationState(params: {
  philosophyProfile: CoachPhilosophyProfile;
  voiceProfile: CoachVoiceProfile;
  status?: CoachIdentityActivationState["status"];
  lastConfirmedAt?: string | null;
  lastActivatedAt?: string | null;
}): CoachIdentityActivationState {
  const explicitStatus = params.status;
  const inferredStatus = params.philosophyProfile.present || params.voiceProfile.present ? "active" : "draft";

  return {
    status: explicitStatus || inferredStatus,
    lastConfirmedAt: params.lastConfirmedAt ?? null,
    lastActivatedAt: params.lastActivatedAt ?? null,
  };
}

export function buildCoachIdentityProfile(params: {
  philosophyProfile: CoachPhilosophyProfile;
  voiceProfile: CoachVoiceProfile;
  references?: CoachIdentityReference[];
  issues?: CoachIdentityIngestionIssue[];
  activationStatus?: CoachIdentityActivationState["status"];
  lastConfirmedAt?: string | null;
  lastActivatedAt?: string | null;
  templateVersion?: string | null;
  rawTemplate?: string | null;
}): CoachIdentityProfile {
  const references = params.references || [];
  return {
    present: params.philosophyProfile.present || params.voiceProfile.present,
    version: "coach_identity_v1",
    philosophy: params.philosophyProfile,
    voice: params.voiceProfile,
    references,
    ingestionReport: buildCoachIdentityIngestionReport({
      philosophyProfile: params.philosophyProfile,
      voiceProfile: params.voiceProfile,
      references,
      issues: params.issues,
    }),
    activation: buildCoachIdentityActivationState({
      philosophyProfile: params.philosophyProfile,
      voiceProfile: params.voiceProfile,
      status: params.activationStatus,
      lastConfirmedAt: params.lastConfirmedAt,
      lastActivatedAt: params.lastActivatedAt,
    }),
    templateVersion: trimText(params.templateVersion),
    rawTemplate: trimText(params.rawTemplate),
  };
}
